// src/components/about/ExperienceDomains.styles.ts
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { device, minDevice } from '../../styles/breakpoints';
export { SectionHeader, Eyebrow, EyebrowMark, Heading, Lede } from './SectionHeader.styles';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/* ---------- Section shell ---------- */

export const DomainsRoot = styled.section`
  padding: ${({ theme }) => theme.spacing['3xl']} 0;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const DomainsContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

/* ---------- Desktop layout ---------- */

export const DomainsLayout = styled.div`
  display: none;

  @media ${minDevice.laptop} {
    display: grid;
    grid-template-columns: 19rem minmax(0, 1fr);
    gap: 3rem;
    align-items: start;
  }
`;

/* ---------- Domain rail (vertical selector) ---------- */

export const DomainRail = styled.nav`
  position: relative;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0.9rem;
    bottom: 0.9rem;
    left: 0;
    width: 1px;
    background: ${({ theme }) => theme.borderColor};
  }
`;

export const RailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const RailItem = styled.button<{ $active: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: start;
  gap: 0.35rem 0.85rem;
  width: 100%;
  padding: 0.95rem 1rem 0.95rem 1.25rem;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0.95rem;
    bottom: 0.95rem;
    left: 0;
    width: 2px;
    background: ${({ $active, theme }) => ($active ? theme.accent : 'transparent')};
    transition: background-color 0.2s ease;
  }

  &:hover {
    transform: none;
    box-shadow: none;
    background: ${({ theme }) => theme.cardBg};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.borderColor};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const RailIndex = styled.span<{ $active: boolean }>`
  font-family: ${monoStack};
  font-size: 0.72rem;
  line-height: 1.7;
  color: ${({ $active, theme }) => ($active ? theme.accent : theme.colors.secondary)};
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};
`;

export const RailIcon = styled.span<{ $active: boolean }>`
  display: inline-flex;
  font-size: 1rem;
  line-height: 1.45;
  color: ${({ $active, theme }) => ($active ? theme.text : theme.colors.secondary)};
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  transition: color 0.2s ease, opacity 0.2s ease;

  svg {
    font-size: 1rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const RailText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

export const RailLabel = styled.span<{ $active: boolean }>`
  font-family: ${sansStack};
  font-size: 0.98rem;
  font-weight: 600;
  color: ${({ $active, theme }) => ($active ? theme.text : theme.colors.secondary)};
  transition: color 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const RailHint = styled.span`
  font-family: ${sansStack};
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.7;
`;

/* ---------- Detail panel ---------- */

export const Panel = styled.div`
  position: relative;
  display: flex;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  @media ${minDevice.laptop} {
    padding: ${({ theme }) => theme.spacing.xl};
    min-height: 30rem;
  }
`;

export const PanelInner = styled(motion.div)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.lg};
`;

export const PanelHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

export const PanelIndex = styled.span`
  font-family: ${monoStack};
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.accent};
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.4rem;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.text};

  @media ${device.mobile} {
    font-size: 1.2rem;
  }
`;

export const PanelDiagram = styled.div`
  flex: 0 0 auto;
  width: 8rem;
  color: ${({ theme }) => theme.accent};
  opacity: 0.8;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  @media ${device.tablet} {
    width: 6.5rem;
  }
`;

export const ContextText = styled.p`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.02rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  @media ${minDevice.laptop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
`;

export const MetaLabel = styled.span`
  font-family: ${monoStack};
  font-size: 0.7rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.7;
`;

export const MetaText = styled.p`
  margin: 0;
  font-family: ${sansStack};
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.text};
`;

export const ConceptsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const ConceptList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Concept = styled.li`
  font-family: ${monoStack};
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  padding: 0.32rem 0.6rem;
  color: ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

/* ---------- Shared stack note ---------- */

export const StackNote = styled.p`
  margin: ${({ theme }) => theme.spacing.xl} 0 0;
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;

/* ---------- Mobile stacked experience ---------- */

export const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};

  @media ${minDevice.laptop} {
    display: none;
  }
`;

export const MobileDomain = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

export const MobileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const MobileHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

export const MobileIconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
`;

export const MobileIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.accent};

  svg {
    font-size: 0.95rem;
  }
`;

export const MobileDiagram = styled.div`
  flex: 0 0 auto;
  width: 5.5rem;
  color: ${({ theme }) => theme.accent};
  opacity: 0.8;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`;